import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Search, Sparkles } from "lucide-react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import FilterBar from "@/components/FilterBar";
import OpportunityCard from "@/components/OpportunityCard";

export default function PublicBoard() {
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState("");
  const [year, setYear] = useState("");
  const [query, setQuery] = useState("");

  useEffect(() => {
    let cancel = false;
    const load = async () => {
      setLoading(true);
      try {
        const params = {};
        if (type) params.type = type;
        if (year) params.year_group = year;
        const { data } = await api.get("/public/opportunities", { params });
        if (!cancel) setOpps(data);
      } catch (_e) {
        if (!cancel) setOpps([]);
      } finally {
        if (!cancel) setLoading(false);
      }
    };
    load();
    return () => { cancel = true; };
  }, [type, year]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return opps;
    return opps.filter((o) =>
      [o.title, o.description, o.provider, o.location].some((s) => s && s.toLowerCase().includes(q))
    );
  }, [opps, query]);

  return (
    <Layout variant="public">
      {/* Hero */}
      <section className="relative overflow-hidden border-b-2 border-black bg-[#FEF3C7]" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 brut-tag bg-white mb-5">
              <Sparkles className="h-3.5 w-3.5" />
              Your next step starts here
            </div>
            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mb-5">
              University open days,<br />
              <span className="bg-[#C8102E] text-white px-2 inline-block border-2 border-black -rotate-1" style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}>apprenticeships</span>,<br />
              and careers events.
            </h1>
            <p className="text-base sm:text-lg text-zinc-800 max-w-xl font-medium">
              Find live opportunities tagged by year group and type. Updated by your careers team — expired events disappear automatically.
            </p>
          </div>
          <div className="lg:col-span-5 flex flex-col items-center gap-5">
            <img
              src="https://customer-assets.emergentagent.com/job_career-hub-538/artifacts/li6pjilk_LC-Crest-%28Transparent%29.png"
              alt="School crest"
              className="h-32 w-32 object-contain hidden lg:block"
            />
            <div className="brut-card-static p-6 bg-white w-full">
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-600 mb-2">Currently live</div>
              <div className="font-display font-black text-6xl mb-1 text-[#C8102E]" data-testid="live-count">{loading ? "—" : opps.length}</div>
              <div className="text-sm font-medium text-zinc-700">opportunities for you to explore</div>
            </div>
          </div>
        </div>
      </section>

      {/* Filters + Search */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-6">
          <div className="relative max-w-xl">
            <Search className="h-4 w-4 absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              type="text"
              placeholder="Search by title, provider, location..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="brut-input pl-11"
              data-testid="search-input"
            />
          </div>
        </div>

        <div className="mb-8">
          <FilterBar type={type} setType={setType} year={year} setYear={setYear} />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20" data-testid="board-loading">
            <Loader2 className="h-8 w-8 animate-spin text-[#C8102E]" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="brut-card-static p-12 text-center" data-testid="empty-state">
            <div className="font-display text-3xl font-black mb-2">Nothing here yet</div>
            <p className="text-zinc-700 font-medium">
              No opportunities match your filters. Try clearing them or check back soon!
            </p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="opportunities-grid">
            {filtered.map((o) => (
              <OpportunityCard key={o.id} opp={o} to={`/opportunity/${o.id}`} />
            ))}
          </div>
        )}
      </section>
    </Layout>
  );
}
